
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';

interface CreateAuthorData {
  name: string;
  email: string;
  phone?: string;
  address?: string;
  taxPercentage: number;
  password: string; // Add password parameter
  paymentMethod: {
    type: 'check' | 'bank_transfer_international' | 'bank_transfer_us' | 'paypal';
    threshold: number;
  };
  paymentDetails?: {
    paypalEmail?: string;
    bankName?: string;
    accountNumber?: string;
    routingNumber?: string;
    swift?: string;
    address?: string;
  };
}

export const useCreateAuthor = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (authorData: CreateAuthorData) => {
      // First, create the user account with the provided password
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: authorData.email,
        password: authorData.password, // Use the provided password
        options: {
          data: {
            name: authorData.name,
          }
        }
      });

      if (authError) {
        throw authError;
      }

      if (!authData.user) {
        throw new Error('Failed to create user account');
      }

      // Create the profile
      const { error: profileError } = await supabase
        .from('profiles')
        .insert({
          id: authData.user.id,
          name: authorData.name,
          email: authorData.email,
          phone: authorData.phone,
          address: authorData.address,
          role: 'author',
          tax_percentage: authorData.taxPercentage,
        });

      if (profileError) {
        throw profileError;
      }

      // Create the payment method
      const { data: paymentMethodData, error: paymentMethodError } = await supabase
        .from('payment_methods')
        .insert({
          author_id: authData.user.id,
          type: authorData.paymentMethod.type,
          threshold: authorData.paymentMethod.threshold,
          is_default: true,
        })
        .select()
        .single();

      if (paymentMethodError) {
        throw paymentMethodError;
      }

      // Create payment details if provided
      if (authorData.paymentDetails && paymentMethodData) {
        const { error: paymentDetailsError } = await supabase
          .from('payment_details')
          .insert({
            author_id: authData.user.id,
            payment_method_id: paymentMethodData.id,
            paypal_email: authorData.paymentDetails.paypalEmail,
            bank_name: authorData.paymentDetails.bankName,
            account_number: authorData.paymentDetails.accountNumber,
            routing_number: authorData.paymentDetails.routingNumber,
            swift: authorData.paymentDetails.swift,
            address: authorData.paymentDetails.address,
          });

        if (paymentDetailsError) {
          throw paymentDetailsError;
        }
      }

      return authData.user;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['authors'] });
      toast({
        title: 'Success',
        description: 'Author has been created successfully.',
      });
    },
    onError: (error) => {
      console.error('Error creating author:', error);
      toast({
        title: 'Error',
        description: 'Failed to create author. Please try again.',
        variant: 'destructive',
      });
    }
  });
};
